import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { trpc } from '@/utils/trpc-client';
import { format } from 'date-fns';
import { clsx } from 'clsx';
import { X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/forms/ui/popover';
import { useFlagEnabled } from '@/utils/feature-flags';
import { NaBadge, StatusBadge, SummaryPills } from './freshness-components';
import type { FreshnessSortableRow, StatusCounts } from './freshness-utils';
import {
  buildSortedRows,
  computeOverallWorst,
  computeStatusCounts,
  formatTimestampWithTz,
  FRESHNESS_QUERY_OPTIONS
} from './freshness-utils';

export function FreshnessIndicator() {
  const globalFreshnessEnabled = useFlagEnabled('global-data-freshness');
  if (!globalFreshnessEnabled) return null;
  return <FreshnessIndicatorInternal />;
}

function FreshnessIndicatorInternal() {
  const [open, setOpen] = useState(false);
  const { data, dataUpdatedAt } = trpc.dataFreshnessStatus.useQuery(
    undefined,
    FRESHNESS_QUERY_OPTIONS
  );

  const activityEnabled = useFlagEnabled('activity-log-v1');
  const buildOptions = useMemo(() => ({ includeActivity: activityEnabled }), [activityEnabled]);

  const statusCounts = useMemo(
    () => (data ? computeStatusCounts(data, buildOptions) : null),
    [data, buildOptions]
  );
  const overallWorst = useMemo(
    () => (statusCounts ? computeOverallWorst(statusCounts) : null),
    [statusCounts]
  );
  const sortedRows = useMemo(
    () => (data ? buildSortedRows(data, buildOptions) : []),
    [data, buildOptions]
  );
  const ariaLabel = useMemo(
    () => (statusCounts ? buildAriaLabel(statusCounts) : 'Data freshness'),
    [statusCounts]
  );
  const lastChecked = useMemo(() => formatTimestampWithTz(dataUpdatedAt), [dataUpdatedAt]);

  if (!data || !statusCounts || !overallWorst) return null;

  return (
    <div className="absolute right-0 top-0 z-10 flex h-12 items-center px-4">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 hover:bg-gray-50">
            <span className="text-xs text-muted-foreground">Data Freshness</span>
            <div role="status" aria-label={ariaLabel} className="flex items-center gap-1.5">
              <SummaryPills counts={statusCounts} ariaHidden />
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-[720px] p-0"
          aria-labelledby="freshness-popover-title"
          onOpenAutoFocus={(e: Event) => e.preventDefault()}>
          <div className="max-h-[70vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div>
                <h3 id="freshness-popover-title" className="text-sm font-semibold">
                  Data Freshness Status
                </h3>
                <p className="text-xs text-muted-foreground">Last checked: {lastChecked}</p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-sm p-1 opacity-70 hover:opacity-100">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </button>
            </div>

            <div className="px-4 py-3">
              <p className="mb-3 text-xs text-muted-foreground">
                Platform and Zoho data sync nightly; the activity log refreshes every few minutes.
              </p>

              {/* Severity alert box */}
              {overallWorst === 'critical' || overallWorst === 'stale' ? (
                <div
                  className={clsx(
                    'mb-3 rounded-lg border px-3 py-2 text-xs font-medium',
                    overallWorst === 'critical' && 'border-red-200 bg-red-50 text-red-700',
                    overallWorst === 'stale' && 'border-amber-200 bg-amber-50 text-amber-700'
                  )}>
                  {overallWorst === 'critical'
                    ? `${statusCounts.critical} critical issue${statusCounts.critical !== 1 ? 's' : ''} detected`
                    : `${statusCounts.stale} source${statusCounts.stale !== 1 ? 's' : ''} stale`}
                </div>
              ) : null}

              {/* Summary pills */}
              <div className="mb-3 flex flex-wrap gap-1.5">
                <SummaryPills counts={statusCounts} />
              </div>

              {/* Platform table */}
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="pb-1.5">Source</th>
                    <th className="pb-1.5">Last Data Date</th>
                    <th className="pb-1.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map(row => (
                    <tr key={row.name} className="border-b last:border-0">
                      <td className="py-2 font-medium">{row.name}</td>
                      <td className="py-2 text-xs text-muted-foreground">
                        {renderPopoverDate(row)}
                      </td>
                      <td className="py-2">{renderPopoverStatus(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Admin link */}
              <div className="mt-3 border-t pt-3">
                <Link
                  to="/admin/data-freshness"
                  onClick={() => setOpen(false)}
                  className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline">
                  View full status page
                </Link>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function renderPopoverDate(row: FreshnessSortableRow): React.ReactNode {
  switch (row.kind) {
    case 'platform':
      return row.lastDataDate ? format(row.lastDataDate, 'MMM d, yyyy') : 'Never';
    case 'zoho':
      return row.lastModified ? formatTimestampWithTz(row.lastModified) : 'Never';
    case 'activity': {
      const showRefresh = row.status === 'stale' || row.status === 'very_stale';
      return (
        <>
          {row.lastActivityAt ? formatTimestampWithTz(row.lastActivityAt) : 'Never'}
          {showRefresh ? (
            <div>
              last run: {row.lastRefreshAt ? formatTimestampWithTz(row.lastRefreshAt) : 'Never'}
            </div>
          ) : null}
        </>
      );
    }
    default: {
      const _exhaustive: never = row;
      return _exhaustive;
    }
  }
}

function renderPopoverStatus(row: FreshnessSortableRow): React.ReactNode {
  switch (row.kind) {
    case 'platform':
      return (
        <div className="flex flex-wrap gap-1">
          <StatusBadge
            status={row.impressions.status}
            amountBehind={row.impressions.daysBehind}
            prefix="Imp"
            pulse={false}
          />
          {row.video ? (
            <StatusBadge
              status={row.video.status}
              amountBehind={row.video.daysBehind}
              prefix="Video"
              pulse={false}
            />
          ) : (
            <NaBadge prefix="Video" />
          )}
        </div>
      );
    case 'zoho':
      return <StatusBadge status={row.status} amountBehind={row.daysBehind} pulse={false} />;
    case 'activity':
      return (
        <StatusBadge status={row.status} amountBehind={row.minutesBehind} unit="m" pulse={false} />
      );
    default: {
      const _exhaustive: never = row;
      return _exhaustive;
    }
  }
}

function buildAriaLabel(counts: StatusCounts): string {
  const parts: string[] = [];
  if (counts.critical > 0) parts.push(`${counts.critical} critical`);
  if (counts.stale > 0) parts.push(`${counts.stale} stale`);
  if (counts.fresh > 0) parts.push(`${counts.fresh} fresh`);
  if (counts.noData > 0) parts.push(`${counts.noData} no data`);
  return parts.length > 0 ? `Data freshness: ${parts.join(', ')}` : 'Data freshness';
}
